import { host } from 'api/web-app';
import { ReactComponent as CloseTagIcon } from 'assets/code-close-tag.svg';
import { ReactComponent as GRPCIcon } from 'assets/grpc-icon.svg';
import { ReactComponent as OpenAPIIcon } from 'assets/openapi-icon.svg';
import DefaultBanner from 'assets/defaultImages/GenericBanner.png';
import { ReactComponent as EnvironmentIcon } from 'assets/environment-icon.svg';
import { ReactComponent as TagIcon } from 'assets/tag-icon.svg';
import { ReactComponent as VersionIcon } from 'assets/version-icon.svg';
import { APIProductVersionSummary } from 'proto/web_app_grpc_pb';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { APIFilter } from './APICatalogSearch';
import { versionParam } from './ApiDetailsPage';
import { getAPITypeName } from '../helper-utils';
import { APIType } from 'proto/github.com/solo-io/dev-portal/api/dev-portal/v1beta1/apiproduct_pb';
// import './APISummaryCard.css';

interface APICardProps {
  content?: {
    name: string;
    namespace: string;
    modifiedDateTime: string;
    displayName: string;
    description: string;
    imageUrl: string;
    versions: APIProductVersionSummary.AsObject[];
  };
  searchFilter?: APIFilter;
}

export const APISummaryCard = ({ content, searchFilter }: APICardProps) => {
  const history = useHistory();

  // Fall back to the default image if we cannot find the selected image for any reason.
  let [cardImage, setCardImage] = React.useState<JSX.Element>(
    <img
      className='h-40 rounded-lg'
      src={`${host}${content?.imageUrl}`}
      alt=''
      onError={() => setCardImage(<img className='h-40 rounded-lg' src={DefaultBanner} alt='' />)}
    />
  );

  const goToVersionDetails = (versionName: string) => () => {
    if (!!content) {
      history.push({
        pathname: `/apis/${content.namespace}/${content.name}`,
        search: `?${versionParam}=${versionName}`
      });
    }
  };

  const filterVersions = (versionInfo: APIProductVersionSummary.AsObject) => {
    if (!searchFilter) {
      return true;
    }
    let apiSpecSatisfied =
      !searchFilter.apiSpecFilter.size ||
      Array.from(searchFilter.apiSpecFilter).includes(getAPITypeName(versionInfo.apiType));
    if (!apiSpecSatisfied) {
      return false;
    }
    let envSatisfied =
      !searchFilter.environmentFilter.size ||
      versionInfo.environmentsList.find(env => searchFilter.environmentFilter.has(env)) !== undefined;
    if (!envSatisfied) {
      return false;
    }

    let versionSatisfied =
      !searchFilter.tagFilter.size || versionInfo.tagsList.find(tag => searchFilter.tagFilter.has(tag)) !== undefined;
    if (!versionSatisfied) {
      return false;
    }

    return true;
  };
  if (!content?.versions || content?.versions.length === 0 || content?.versions?.filter(filterVersions).length === 0) {
    return null;
  }

  return (
    <div className='p-6 overflow-hidden bg-white border border-gray-400 rounded-lg api-card-block '>
      <div className='flex space-x-2 api-card-product-details'>
        <div className='flex-shrink-0 w-1/4 mr-2 rounded-lg api-card-api-image-holder'>{cardImage}</div>
        <div className='flex flex-col api-card-content-block'>
          <div className='text-2xl font-medium text-gray-900 api-card-content-name'>{content?.displayName}</div>
          <span className='flex flex-col justify-between h-full mt-1'>
            <div className='flex-grow h-full text-base leading-tight text-gray-700 api-card-content-description'>
              {content?.description}
            </div>

            <div className='mb-1 text-base text-gray-600 api-card-content-date place-self-end'>
              Modified: {content?.modifiedDateTime}
            </div>
          </span>
        </div>
      </div>
      <div className='mt-3 space-y-3 api-card-versions'>
        {content?.versions
          ?.filter(filterVersions)
          .sort((a, b) => (a.name === b.name ? 0 : a.name > b.name ? 1 : -1))
          .map(versionInfo => (
            <div
              className='flex items-center p-2 px-3 space-x-4 bg-white border border-gray-400 divide-x rounded-lg cursor-pointer hover:bg-gray-100 api-card-version-container'
              key={versionInfo.name}
              onClick={goToVersionDetails(versionInfo.name)}>
              <div className='flex items-center space-x-3 api-card-version-summary'>
                <span className='flex items-center w-24 space-x-2'>
                  {versionInfo.apiType === APIType.GRPC ? (
                    <GRPCIcon className='w-16 ' />
                  ) : (
                    <OpenAPIIcon className='h-8 w-21' />
                  )}
                </span>
                <span className='flex items-center space-x-2'>
                  <VersionIcon className='w-6 h-6 fill-current text-blue-500gloo' />
                  <span className='ml-1 text-base text-gray-900 api-card-version-name'> {versionInfo.name} </span>
                </span>
                <span className='flex items-center space-x-1'>
                  <CloseTagIcon className='w-6 h-6 fill-current text-blue-500gloo' />
                  <span className='ml-1 text-base text-gray-900 '> {versionInfo.operationsCount}</span>
                  <span>Operations</span>{' '}
                </span>
              </div>
              <div className='flex items-center flex-grow space-x-4 api-card-version-details'>
                <div className={'ml-4 api-card-version-environments flex items-center justify-center space-x-1'}>
                  <span>
                    <EnvironmentIcon className='w-4 h-4 text-gray-900 fill-current ' />
                  </span>
                  <span className='flex flex-wrap items-center flex-grow space-x-1'>
                    {!!versionInfo?.environmentsList?.length &&
                      versionInfo?.environmentsList.sort().map(environment => (
                        <span
                          className={`px-2 mt-1 text-sm font-medium  bg-white border-gray-400  border rounded-full api-card-searchable-name ${
                            searchFilter?.environmentFilter.has(environment)
                              ? 'border-blue-500gloo bg-blue-200gloo text-blue-500gloo'
                              : ''
                          } `}
                          key={environment}>
                          {environment}
                        </span>
                      ))}
                  </span>
                </div>
                <div className='flex items-center justify-center space-x-1 api-card-version-tags'>
                  {!!versionInfo?.tagsList?.length && (
                    <span>
                      <TagIcon className='w-4 h-4 fill-current ' />
                    </span>
                  )}
                  <span className='flex flex-wrap items-center flex-grow-0 space-x-1 overflow-hidden'>
                    {!!versionInfo?.tagsList?.length &&
                      versionInfo?.tagsList.sort().map(tag => (
                        <span
                          className={`px-2 mt-1 border bg-white text-gray-800 font-medium text-sm rounded-full border-gray-400  api-card-searchable-name ${
                            searchFilter?.tagFilter.has(tag)
                              ? 'border-blue-500gloo bg-blue-200gloo text-blue-500gloo'
                              : ''
                          } `}
                          key={tag}>
                          {tag}
                        </span>
                      ))}
                  </span>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
